import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://marketsreview.org';
const SITE_NAME = 'MarketsReview.org';
const DEFAULT_DESCRIPTION = 'Independent Forex & CFD broker comparison platform. Compare regulation, fees, platforms and risk profiles across leading global brokers.';
const DEFAULT_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/41d20fa1-ee12-4f12-94a3-45363f4f073e/images/dabcc4d3f075c4f70f1b991b0d78919e4de455e7f04776f2b668362ab37be4b7.png';

export function SEO({ title, description, canonical, image, type = 'website', jsonLd, noindex = false, robots }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Independent Forex & CFD Broker Reviews`;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const img = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots || (noindex ? 'noindex, nofollow' : 'index, follow')} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (Array.isArray(jsonLd)
        ? jsonLd.map((item, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(item)}</script>
          ))
        : <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// JSON-LD Schema Builders
export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MarketsReview.org',
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@vipaffiliatenetwork.com',
      contactType: 'customer service'
    }
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/guides?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
}

export function buildBrokerReviewSchema(broker) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    name: `${broker.name} Review ${new Date().getFullYear()}`,
    reviewBody: broker.verdict,
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    itemReviewed: {
      '@type': 'FinancialService',
      name: broker.name,
      description: broker.short_description,
      url: broker.affiliate_url,
      foundingDate: broker.founded,
      address: {
        '@type': 'PostalAddress',
        addressLocality: broker.headquarters
      }
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: broker.score,
      bestRating: 5,
      worstRating: 1
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL
    }
  };
}

export function buildArticleSchema(article) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image_url,
    datePublished: article.created_at,
    dateModified: article.updated_at,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/guides/${article.slug}`
    }
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined
    }))
  };
}

export function buildComparisonSchema(brokers) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Forex & CFD Broker Comparison',
    description: 'Independent comparison of forex and CFD brokers',
    numberOfItems: brokers.length,
    itemListElement: brokers.map((broker, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'FinancialService',
        name: broker.name,
        description: broker.short_description,
        url: `${BASE_URL}/${broker.slug}`
      }
    }))
  };
}
